import { Link } from "react-router-dom";
import type { GossipItem } from "@/mocks/gossipList";

export function statusClass(status: string): string {
  switch (status) {
    case "持续发酵":
      return "bg-primary-100 text-primary-800 border-primary-200";
    case "待回应":
      return "bg-accent-100 text-accent-900 border-accent-300";
    case "已澄清":
      return "bg-secondary-100 text-secondary-800 border-secondary-200";
    default:
      return "bg-background-200 text-foreground-700 border-background-300";
  }
}

export function formatHeat(heat: number): string {
  return heat >= 10000 ? `${(heat / 10000).toFixed(1)} 万` : `${heat}`;
}

interface GossipCardProps {
  item: GossipItem;
  variant?: "grid" | "row";
}

export default function GossipCard({ item, variant = "grid" }: GossipCardProps) {
  const detailPath = `/topic/${item.id}`;

  if (variant === "row") {
    return (
      <article className="group bg-background-100 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors">
        <Link to={detailPath} className="flex flex-col sm:flex-row items-stretch cursor-pointer">
          <div className="hidden sm:flex w-16 shrink-0 items-center justify-center bg-background-200/70 font-heading font-black text-2xl text-foreground-400 group-hover:text-primary-500 transition-colors">
            {String(item.rank).padStart(2, "0")}
          </div>
          <div className="relative w-full sm:w-52 h-44 sm:h-auto min-h-[150px] shrink-0 overflow-hidden">
            <img
              src={item.cover}
              alt={`${item.title} - 瓜网30事件盘点`}
              title={`${item.title} - 瓜网30`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="flex-1 flex flex-col gap-2 p-4 md:p-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 border border-accent-200">
                {item.category}
              </span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${statusClass(item.status)}`}>
                {item.status}
              </span>
              <span className="text-[11px] text-foreground-500">{item.date}</span>
            </div>
            <h3 className="font-heading font-bold text-base md:text-lg text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
              {item.title}
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{item.summary}</p>
            <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-3-line text-foreground-400"></i>
                {item.people.join(" · ")}
              </span>
              <span className="flex items-center gap-1 text-primary-600">
                <i className="ri-fire-line"></i>
                {formatHeat(item.heat)}
              </span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group flex flex-col bg-background-100 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors">
      <Link to={detailPath} className="flex flex-col h-full cursor-pointer">
        <div className="relative w-full h-44 overflow-hidden shrink-0">
          <img
            src={item.cover}
            alt={`${item.title} - 瓜网30事件盘点`}
            title={`${item.title} - 瓜网30`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute top-3 left-3 w-9 h-9 flex items-center justify-center rounded-md bg-primary-500/90 backdrop-blur text-background-50 font-heading font-black text-sm">
            {String(item.rank).padStart(2, "0")}
          </span>
          <span className="absolute top-3 right-3 text-[11px] px-2 py-0.5 rounded-full bg-background-50/90 text-foreground-800">
            {item.category}
          </span>
        </div>
        <div className="flex flex-col flex-1 gap-2 p-4">
          <div className="flex items-center gap-2">
            <span className={`text-[11px] px-2 py-0.5 rounded-full border ${statusClass(item.status)}`}>
              {item.status}
            </span>
            <span className="text-[11px] text-foreground-500">{item.date}</span>
          </div>
          <h3 className="font-heading font-bold text-base text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors line-clamp-2">
            {item.title}
          </h3>
          <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-3">{item.summary}</p>
          <div className="mt-auto pt-2 flex items-center justify-between text-xs">
            <span className="text-foreground-500 truncate max-w-[60%]">{item.people.join(" · ")}</span>
            <span className="flex items-center gap-1 text-primary-600 shrink-0">
              <i className="ri-fire-line"></i>
              {formatHeat(item.heat)}
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}